import { memo } from 'react'
import { Skeleton } from '@mui/material'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { covertMinutesToHrs, formatFlightDateTime, usePackageInfo } from '@/utils'
import { useTranslations } from '@/core/translation'
import { Box, Text, Divider } from '@/components'
import { flightInfoStyles as styles } from './FlightInfo.style'
import { flightInfoClasses, FLIGHT_LIST_TIME_FORMAT } from './FlightInfo.data'
import { FlightInfoProps } from './FlightInfo.types'
import { useMemo } from 'react'

const FlightInfo = ({
  smallFonts,
  airport,
  duration,
  departureDateTime,
  arrivalDateTime,
  stops,
  isInReturnFlightWorkflow,
  isFromFlightList = false,
}: FlightInfoProps) => {
  const t = useTranslations('flights')
  const { currentPackage } = usePackageInfo()
  const { flightParams } = currentPackage || {}

  const dateToCompare = isInReturnFlightWorkflow
    ? flightParams?.returnDate
    : flightParams?.departureDate

  const daysDiff = useMemo(
    () =>
      isFromFlightList && departureDateTime && arrivalDateTime
        ? dayjs(arrivalDateTime).startOf('day').diff(dayjs(departureDateTime).startOf('day'), 'day')
        : 0,
    [arrivalDateTime, departureDateTime, isFromFlightList],
  )

  return (
    <Box sx={styles.flightInfo} className={clsx({ [flightInfoClasses.smallFonts]: smallFonts })}>
      <Box sx={styles.airportWrapper}>
        <Text sx={styles.airportCode}>{airport.departure}</Text>
        <Text sx={styles.flightTimings}>
          {formatFlightDateTime({
            date: departureDateTime,
            ...(isFromFlightList && {
              referenceDate: dayjs(dateToCompare ?? ''),
              dateFormat: FLIGHT_LIST_TIME_FORMAT,
            }),
          })}
        </Text>
      </Box>

      <Divider sx={styles.divider} />

      <Box sx={styles.stopDetails}>
        <Text sx={styles.stopLabel}>{t('stops', { count: stops })}</Text>
        <Text sx={styles.flightDuration}>{covertMinutesToHrs(duration)}</Text>
        {!smallFonts && <Text sx={styles.seeDetails}>{t('seeDetails')}</Text>}
      </Box>

      <Divider sx={styles.divider} />

      <Box sx={styles.airportWrapper}>
        <Text sx={styles.airportCode}>{airport.destination}</Text>
        <Text sx={styles.flightTimings}>
          {formatFlightDateTime({
            date: arrivalDateTime,
            ...(isFromFlightList && {
              referenceDate: dayjs(dateToCompare ?? ''),
              dateFormat: FLIGHT_LIST_TIME_FORMAT,
            }),
          })}
          {daysDiff > 0 && (
            <Text component="span" sx={styles.seeDetails}>
              {' '}
              +{daysDiff}
            </Text>
          )}
        </Text>
      </Box>
    </Box>
  )
}

export default memo(FlightInfo)

export function FlightInfoLoader() {
  return (
    <Box sx={styles.flightInfo}>
      <Box sx={styles.airportWrapper}>
        <Skeleton variant="text" sx={styles.airportCodeLoader} />
        <Skeleton variant="text" sx={styles.flightTimingsLoader} />
      </Box>

      <Divider sx={styles.divider} />

      <Box sx={styles.stopDetails}>
        <Skeleton variant="text" sx={styles.stopLabelLoader} />
        <Skeleton variant="text" sx={styles.flightDurationLoader} />
        <Skeleton variant="text" sx={styles.seeDetailsLoader} />
      </Box>

      <Divider sx={styles.divider} />

      <Box sx={styles.airportWrapper}>
        <Skeleton variant="text" sx={styles.airportCodeLoader} />
        <Skeleton variant="text" sx={styles.flightTimingsLoader} />
      </Box>
    </Box>
  )
}
